/* eslint-disable @typescript-eslint/restrict-template-expressions */

import styled from "@emotion/styled";
import { cssBaseBackgroundVar, cssBaseBorderVar, cssBaseTextVar } from "../constants";
import { BaseButtonStyledProps } from "../types";
import { getButtonBackgroundColor, getButtonBackgroundColorVar } from "./getButtonBackgroundColor";
import { getButtonBorderColor, getUnderlineOffset } from "./getButtonBorder";
import { getButtonBorderRadius } from "./getButtonBorderRadius";
import { getButtonFocusRingStyles } from "./getButtonFocusRingStyles";
import { getButtonFontSize, getButtonFontWeight, getButtonLineHeight } from "./getButtonFontStyles";
import { getButtonHeight } from "./getButtonHeight";
import { getButtonMinWidth } from "./getButtonMinWidth";
import { getButtonMotion } from "./getButtonMotion";
import { getButtonPadding } from "./getButtonPadding";
import { getButtonTextColor, getButtonTextColorVar } from "./getButtonTextColor";
import { getTextDecoration } from "./getButtonTextDecoration";

export const BaseButtonStyled = styled("button")<BaseButtonStyledProps>(
    (props) => `
    text-transform: none; // Remove inheritance of text transform in Firefox
    overflow: visible; // Show overflow in Edge
    margin: 0; // Remove margin in Firefox and Safari
    padding: 0; // Remove padding in webkit
    outline: none; // Remove default focus outline in various browsers
    font-family: inherit; // Ensure we inherit from parent since many browsers override this in user-agent stylesheet.

    &::-moz-focus-inner {
        border: 0;
        margin: 0;
        padding: 0;
    }

    display: inline-flex;
    box-sizing: border-box;
    align-items: center;

    height: ${
        props.appearance !== "link" ? `var(--sds--button--height--${props.size}, ${getButtonHeight(props)}px)` : "auto"
    };
    ${props.shouldFitContainer ? "width: 100%;" : ""};
    min-width: ${getButtonMinWidth(props)};
    max-width: 100%; // if the parent is smaller than the button, we want to truncate
    padding: ${
        getButtonPadding(props) as string
    }; // switch case in this function has no default, but all cases are covered. Casting here prevents a potential 'undefined' type which the linter complains about in a template literal
    border-width: 1px;
    border-style: solid;
    border-color: var(${cssBaseBorderVar}, ${getButtonBorderColor(props)});
    text-align: center;
    border-radius: var(--sds--button--border-radius--${props.size}, ${getButtonBorderRadius(props)});
    background-color: var(${getButtonBackgroundColorVar(
        props.state,
        props.focusMode
    )}, var(${cssBaseBackgroundVar}, ${getButtonBackgroundColor(props)}));
    font-size: ${getButtonFontSize(props)};
    line-height: ${getButtonLineHeight(props)};
    font-weight: ${getButtonFontWeight(props)};
    color: var(${getButtonTextColorVar(props.state)}, var(${cssBaseTextVar}, ${getButtonTextColor(props)}));
    --sds--ursa-icons--text-color: var(${getButtonTextColorVar(
        props.state
    )}, var(${cssBaseTextVar}, ${getButtonTextColor(props)}));
    white-space: nowrap;
    cursor: ${props.state.isDisabled ? "not-allowed" : "pointer"};
    text-decoration: ${getTextDecoration(props)}; // if the href prop is used, an anchor tag will be rendered instead
    position: relative;
    transition: ${getButtonMotion(props)};
    text-underline-offset: ${getUnderlineOffset(props)};
    ${getButtonFocusRingStyles(props)}
`
);
